import { useRef, type InputHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { uppercaseName } from '../shared/names';

export function uppercaseNameControl(
	control: Pick<HTMLInputElement, 'value' | 'selectionStart' | 'selectionEnd' | 'selectionDirection' | 'setSelectionRange'>,
): boolean {
	const original = control.value;
	const next = uppercaseName(original);
	if (next === original) return false;
	const { selectionStart, selectionEnd, selectionDirection } = control;
	control.value = next;
	if (selectionStart !== null && selectionEnd !== null) {
		control.setSelectionRange(
			uppercaseName(original.slice(0, selectionStart)).length,
			uppercaseName(original.slice(0, selectionEnd)).length,
			selectionDirection ?? undefined,
		);
	}
	return true;
}

function useNameEntry(onValueChange?: (value: string) => void) {
	const composing = useRef(false);
	return {
		onChange: (event: { currentTarget: HTMLInputElement | HTMLTextAreaElement }) => {
			if (!composing.current) uppercaseNameControl(event.currentTarget);
			onValueChange?.(event.currentTarget.value);
		},
		onCompositionStart: () => {
			composing.current = true;
		},
		onCompositionEnd: (event: { currentTarget: HTMLInputElement | HTMLTextAreaElement }) => {
			composing.current = false;
			uppercaseNameControl(event.currentTarget);
			onValueChange?.(event.currentTarget.value);
		},
		onBlur: (event: { currentTarget: HTMLInputElement | HTMLTextAreaElement }) => {
			if (uppercaseNameControl(event.currentTarget)) onValueChange?.(event.currentTarget.value);
		},
	};
}

export function NameInput({
	onValueChange,
	...props
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'onBlur' | 'type'> & { onValueChange?: (value: string) => void }) {
	const handlers = useNameEntry(onValueChange);
	return <input {...props} type="text" autoCapitalize="characters" {...handlers} />;
}

export function NameTextarea({
	onValueChange,
	...props
}: Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'onChange' | 'onBlur'> & { onValueChange?: (value: string) => void }) {
	const handlers = useNameEntry(onValueChange);
	return <textarea {...props} autoCapitalize="characters" {...handlers} />;
}
